import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { brand, sockJsUrl } from '../config/brands';
import './Sidebar.css';
import {
    AdjustmentsHorizontalIcon,
    ArrowLongLeftIcon,
    ArrowLongRightIcon,
    Bars3Icon,
    HomeIcon,
    BuildingOfficeIcon,
    PercentBadgeIcon,
    PhotoIcon,
    Squares2X2Icon,
    ArchiveBoxIcon,
    ChevronDownIcon,
    ShoppingBagIcon,
    UsersIcon,
    TruckIcon,
    EnvelopeIcon,
    CogIcon,
    BellAlertIcon,
    ArrowRightStartOnRectangleIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { findBusinessByPhoneNumber, getAllOrdersByBusiness, getAllPendingOrders } from '../adapter/api';
import SockJsClient from 'react-stomp';

const Sidebar = () => {
    const location = useLocation(); // Get the current location/path
    const [userRole, setUserRole] = useState(null);
    const [business, setBusiness] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 769); // Drawer on mobile, collapsible sidebar on desktop
    const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
    const SOCKET_URL = sockJsUrl();
    const [isConnected, setIsConnected] = useState(false);
    const clientRef = useRef(null);
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(() => ['/notifications', '/listNotifications'].includes(location.pathname));

    const toggleSubmenu = () => {
        setIsSubmenuOpen((prev) => !prev);
    };

    // Update this state whenever a new order is received
    const onMessageReceived = (msg) => {
        setPendingOrdersCount((prevCount) => prevCount + 1);
        //playSound()
    };

    // Function to check if the link is active based on the current path
    const isActive = (path) => location.pathname === path;
    const logout = () => {
        localStorage.clear();
        window.location.assign('/login');
    };

    const onConnect = async () => {
        console.log('Connected to WebSocket');
        setIsConnected(true);  // Update state when connected
    };

    const onDisconnect = async () => {
        console.log('Disconnected from WebSocket');
        setIsConnected(false);  // Update state when disconnected
    };

    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const user = await AsyncStorage.getItem("myUser");
                if (user) {
                    const parsedUser = JSON.parse(user);
                    const { role, email } = parsedUser;

                    setUserRole(role); // Set the role from AsyncStorage



                    if (role === 'ROLE_BUSINESS') {
                        const response2 = await getAllOrdersByBusiness(email); // Use `email` directly
                        setPendingOrdersCount(response2.data.data.length);
                        const response3 = await findBusinessByPhoneNumber(email)

                        setBusiness(response3.data.data)
                    } else if (role === 'ROLE_ADMIN') {
                        const response2 = await getAllPendingOrders();
                        setPendingOrdersCount(response2.data.data.length);
                    }
                }
            } catch (error) {
                console.error("Error fetching user role or orders:", error);
            } finally {

            }
        };

        fetchUserRole();
    }, []);

    // Example of sending a message after connection is established
    useEffect(() => {
        if (isConnected) {
            console.log('WebSocket connected, now you can send messages.');
        }
    }, [isConnected]);

    useEffect(() => {
        if (window.innerWidth < 769) setIsSidebarOpen(false);
    }, [location.pathname]);

    const notificationsActive = ['/notifications', '/listNotifications'].includes(location.pathname);
    const groups = [
        { label: 'Workspace', items: [
            ['Dashboard', '/dashboard', HomeIcon],
            ['Pending Orders', '/pending-orders', Bars3Icon],
            ['All Orders', '/all-orders', Squares2X2Icon],
        ] },
        { label: 'Catalog', items: [
            ['Categories', '/categories', ArchiveBoxIcon, true],
            ['Subcategories', '/subCategories', AdjustmentsHorizontalIcon, true],
            ['Businesses', '/businesses', BuildingOfficeIcon, true],
            ['Menu', '/menu', ShoppingBagIcon],
        ] },
        { label: 'People', items: [
            ['Customers', '/customers', UsersIcon],
            ['Drivers', '/drivers', TruckIcon, true],
        ] },
        { label: 'Engagement', items: [
            ['Banners', '/banners', PhotoIcon, true],
            ['Promo Code', '/promoCode', PercentBadgeIcon, true],
            ['Notifications', 'notifications-menu', BellAlertIcon, true],
        ] },
        { label: 'Management', items: userRole === 'ROLE_BUSINESS' ? [
            ['Settings', '/settings', CogIcon],
        ] : [
            ['Support', '/support', EnvelopeIcon],
            ['App Details', '/app-details', CogIcon],
        ] },
    ];
    const renderLink = ([label, path, Icon]) => (
        <a key={path} href={path} className={`sb-link ${isActive(path) ? 'is-active' : ''}`}
            aria-label={label} title={!isSidebarOpen ? label : undefined}
            aria-current={isActive(path) ? 'page' : undefined}>
            <Icon aria-hidden="true" />
            <span className="sb-label">{label}</span>
            {path === '/pending-orders' && pendingOrdersCount > 0 && <span className="sb-count">{pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}</span>}
            {isActive(path) && <ChevronRightIcon className="sb-active-arrow" aria-hidden="true" />}
        </a>
    );

    return (
        <>
            <button type="button" className="mobile-menu-button" onClick={() => setIsSidebarOpen(true)}
                aria-label="Open navigation menu" aria-controls="main-sidebar" aria-expanded={isSidebarOpen}>
                <Bars3Icon className="h-6 w-6" /><img src={brand.navigationLogo} alt={`${brand.displayName} logo`} />
            </button>
            {isSidebarOpen && <button type="button" className="sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} aria-label="Close navigation menu" />}
            <div className={`sidebar-space sb-space ${isSidebarOpen ? 'is-open' : 'is-closed'}`}>
                <SockJsClient url={SOCKET_URL} topics={['/topic/message']} onMessage={onMessageReceived}
                    onConnect={onConnect} onDisconnect={onDisconnect} onError={(error) => console.error('WebSocket error:', error)} debug={false} ref={clientRef} />
                <aside id="main-sidebar" className={`app-sidebar sb-shell ${isSidebarOpen ? 'is-open' : 'is-closed'}`} aria-label="Main sidebar">
                    <header className="sb-header">
                        <a href="/dashboard" className="sb-brand" aria-label={`${brand.displayName} dashboard`}>
                            <img src={brand.navigationLogo} alt={`${brand.displayName} logo`} />
                          
                        </a>
                        <button type="button" className="sb-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            aria-label={isSidebarOpen ? 'Collapse navigation' : 'Expand navigation'} aria-expanded={isSidebarOpen}>
                            {isSidebarOpen ? <ArrowLongLeftIcon /> : <ArrowLongRightIcon />}
                        </button>
                    </header>
                    <nav className="sb-nav" aria-label="Main navigation">
                        {groups.map(group => {
                            const items = group.items.filter(item => !item[3] || userRole !== 'ROLE_BUSINESS');
                            if (!items.length) return null;
                            return <section className="sb-group" key={group.label} aria-label={group.label}>
                                <h2 className="sb-group-title">{group.label}</h2>
                                {items.map(item => item[1] !== 'notifications-menu' ? renderLink(item) : (
                                    <div key={item[1]}>
                                        <button type="button" className={`sb-link ${notificationsActive ? 'is-active' : ''}`}
                                            aria-label="Notifications" title={!isSidebarOpen ? 'Notifications' : undefined}
                                            aria-expanded={isSubmenuOpen} aria-controls="sidebar-notifications"
                                            onClick={() => { if (!isSidebarOpen) { setIsSidebarOpen(true); setIsSubmenuOpen(true); } else toggleSubmenu(); }}>
                                            <BellAlertIcon aria-hidden="true" /><span className="sb-label">Notifications</span>
                                            <ChevronDownIcon className={`sb-chevron ${isSubmenuOpen ? 'expanded' : ''}`} />
                                        </button>
                                        {isSubmenuOpen && isSidebarOpen && <div id="sidebar-notifications" className="sb-submenu">
                                            <a href="/notifications" aria-current={isActive('/notifications') ? 'page' : undefined}>Send Notification</a>
                                            <a href="/listNotifications" aria-current={isActive('/listNotifications') ? 'page' : undefined}>View Notifications</a>
                                        </div>}
                                    </div>
                                ))}
                            </section>;
                        })}
                    </nav>
                    <footer className="sb-footer">
                        <div className="sb-account">
                            <div className="sb-avatar">{business.logo ? <img src={business.logo} alt="" /> : <BuildingOfficeIcon />}</div>
                            <div className="sb-account-text"><strong>{business.name || brand.displayName}</strong><span>{userRole === 'ROLE_BUSINESS' ? 'Business workspace' : 'Admin workspace'}</span></div>
                        </div>
                        <button type="button" className="sb-link sb-logout" onClick={logout} aria-label="Logout" title={!isSidebarOpen ? 'Logout' : undefined}>
                            <ArrowRightStartOnRectangleIcon /><span className="sb-label">Logout</span>
                        </button>
                    </footer>
                </aside>
            </div>
        </>
    );
};

export default Sidebar;
